import { Link } from "react-router-dom";
import { useReveal } from "../../../hooks/useReveal";
import { allServices } from "../../../mocks/services";

const detailedServices = [
  {
    title: "Newborn Photography",
    tag: "First Days",
    desc: "Welcoming a new life is the most extraordinary event a family experiences. Stephanie's newborn sessions are conducted with patience, warmth, and an unwavering commitment to your baby's safety and comfort. Sessions typically span 2–4 hours and can span multiple visits if needed.",
    details: ["In-studio and on-location available", "Curated wardrobe collection provided", "Multiple session days accommodated", "Heirloom print product selection", "Private online gallery delivery"],
    image: "https://public.readdy.ai/ai/img_res/18cfe3f4e0c29813bdce217ee73b5d1b.jpg",
    reverse: false,
  },
  {
    title: "Luxury Weddings",
    tag: "Your Day, Forever",
    desc: "Your wedding is a singular chapter in your love story. Precious Keepsakes delivers fine art wedding photography that weaves together cinematic imagery and documentary storytelling — preserving not just the visuals, but the feeling of your entire day.",
    details: ["Full-day and partial day coverage", "Engagement session option", "Second shooter available", "Fine art album design", "Drone imagery available"],
    image: "https://images-pw.pixieset.com/site/BAJyQr/wdadAo/B1B-328cb125-1500.jpg",
    reverse: true,
  },
  {
    title: "Milestones & Memories",
    tag: "Every Chapter",
    desc: "From the glow of maternity to the confidence of senior portraits, from the festivity of a quinceañera to the intimacy of a family session — Precious Keepsakes captures every milestone with the same level of care and artistry.",
    details: ["Maternity sessions", "Senior portraits", "Quinceañera & Sweet 16", "Family lifestyle sessions", "On-location throughout San Antonio"],
    image: "https://images-pw.pixieset.com/site/BAJyQr/0PjQvm/18-430f468f-1500.jpg",
    reverse: false,
  },
];

export default function ServicesDetail() {
  const ref = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="bg-pk-ivory py-28" id="services-detail">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="space-y-32">
          {detailedServices.map((s, i) => (
            <div
              key={i}
              className={`grid grid-cols-1 md:grid-cols-2 gap-16 items-center ${s.reverse ? "md:grid-flow-dense" : ""}`}
            >
              {/* Image */}
              <div className={`reveal-${s.reverse ? "right" : "left"} relative ${s.reverse ? "md:col-start-2" : ""}`}>
                <div className="w-full h-[560px] overflow-hidden rounded-sm img-hover-zoom">
                  <img
                    src={s.image}
                    alt={s.title + " San Antonio"}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {/* Offset accent border */}
                <div className={`absolute -bottom-5 ${s.reverse ? "-left-5" : "-right-5"} w-full h-full border border-pk-gold/15 rounded-sm -z-10`} />
              </div>

              {/* Content */}
              <div className={`reveal-${s.reverse ? "left" : "right"} ${s.reverse ? "md:col-start-1 md:row-start-1" : ""}`}>
                <span className="font-sans text-xs tracking-widest3 uppercase text-pk-gold">{s.tag}</span>
                <h2 className="font-display text-5xl md:text-6xl font-medium text-pk-charcoal mt-3 leading-tight">
                  {s.title}
                </h2>
                <div className="w-10 h-px bg-pk-gold mt-5 mb-6" />
                <p className="font-sans text-sm text-pk-muted leading-7">{s.desc}</p>
                <ul className="mt-8 space-y-3">
                  {s.details.map((d, j) => (
                    <li key={j} className="flex items-center gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-line text-pk-gold" />
                      </div>
                      <span className="font-sans text-sm text-pk-charcoal/80">{d}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#inquire"
                  className="mt-10 inline-flex items-center gap-2 bg-pk-gold text-white font-sans text-xs tracking-widest3 uppercase px-10 py-4 rounded-sm hover:bg-pk-charcoal transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Inquire About This Session
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </div>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* All services pill list */}
        <div className="reveal mt-24 pt-16 border-t border-pk-light">
          <p className="font-sans text-xs tracking-widest3 uppercase text-pk-gold text-center mb-8">We Also Offer</p>
          <div className="flex flex-wrap justify-center gap-3">
            {allServices.map((s) => (
              <Link
                key={s.title}
                to={s.href}
                className="font-sans text-xs tracking-wide px-6 py-3 border border-pk-light text-pk-charcoal rounded-sm hover:border-pk-gold hover:text-pk-gold transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                {s.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
